import React, {type RefObject, useCallback, useRef, useState} from 'react';
import {twMerge} from 'tailwind-merge';
import useResizeObserver from 'use-resize-observer';

import {type MediaSize} from '../../ImageCard/resolveMediaSize';
import {useStablePanelId} from '../../Panel/PanelIDContext';
import {getKeyForTileMediaSpec} from '../fetch/keys';
import {
  type DragSummary,
  useScrollCenterPan,
  type ViewportCenterPercent,
} from '../hooks/useScrollCenterPan';
import {containFit} from '../sizing/containFit';
import {zoomedSize} from '../sizing/zoomedSize';
import {TileMediaLoader} from '../TileMediaLoader';
import {selectViewport, useCompareViewportStore} from './compareViewportStore';

type Props = React.ComponentProps<typeof TileMediaLoader>;

const isUsable = (size: {width?: number; height?: number}) =>
  Number.isFinite(size.width) &&
  Number.isFinite(size.height) &&
  (size.width as number) > 0 &&
  (size.height as number) > 0;

/** Pointer slop: a press that moves no farther still counts as a click. */
const DRAG_CLICK_SUPPRESSION_PX = 4;

/**
 * Runs the fullscreen modal's sizing engine inside one synced Compare tile:
 * at Fit the image is contain-fitted to the measured viewport, and at a
 * numeric zoom from the shared viewport store it renders at natural size
 * times zoom inside a scroll container whose viewport center survives zoom
 * changes. Layout card dimensions are estimates; the image's logged history
 * size is used until the loaded image reports its resolved size through
 * onImageRender, mirroring how the fullscreen modal sizes.
 */
export const CompareFitTile: React.FC<Props> = props => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const {width, height} = useResizeObserver<HTMLDivElement>({
    ref: viewportRef as RefObject<HTMLDivElement>,
  });
  const specKey = getKeyForTileMediaSpec(props.tileMediaSpec);
  const [reported, setReported] = useState<{
    key: string;
    size: MediaSize;
  } | null>(null);
  const onImageRender = useCallback(
    (size: MediaSize) => setReported({key: specKey, size}),
    [specKey]
  );

  // A size reported by the previous media never sizes its replacement.
  const resolved = reported?.key === specKey ? reported.size : null;
  const history = {width: props.mediaWidth, height: props.mediaHeight};
  const dims = resolved ?? (isUsable(history) ? (history as MediaSize) : null);
  const container = {
    width: width ?? props.width,
    height: height ?? props.height,
  };
  const fit =
    dims != null && isUsable(container) ? containFit(dims, container) : null;

  const panelId = useStablePanelId();
  const zoom = useCompareViewportStore(
    state => selectViewport(panelId)(state).zoom
  );
  // Both null checks narrow together: fit exists exactly when dims do.
  const measured = dims != null && fit != null;
  let cardSize: MediaSize = {width: props.width, height: props.height};
  if (measured) {
    if (zoom === 'fit') {
      cardSize = {width: fit.width, height: fit.height};
    } else {
      cardSize = zoomedSize(dims, zoom);
    }
  }

  // Beyond-fit zoom turns the tile into the modal's scroll container, and
  // dragging any tile pans every tile: the shared center is the content
  // fraction at the viewport center, so each tile derives its own scroll
  // offsets from it and clamps at its own edges. The dragging tile's scrolls
  // and user wheel scrolls write the shared center; the hook's own clamped
  // restores are discarded so a small tile at its edge never truncates the
  // shared value. Fit resets the center in the store.
  const overflowsX = cardSize.width > container.width;
  const overflowsY = cardSize.height > container.height;
  const canPan = overflowsX || overflowsY;
  const sharedCenter = useCompareViewportStore(
    state => selectViewport(panelId)(state).center
  );
  const setCenter = useCompareViewportStore(state => state.setCenter);
  // A drag carries no opinion on an axis it cannot move: only axes this
  // tile's own container overflows reach the shared center.
  const writeExpressibleAxes = useCallback(
    (el: HTMLDivElement, next: ViewportCenterPercent) => {
      const current = selectViewport(panelId)(
        useCompareViewportStore.getState()
      ).center;
      setCenter(panelId, {
        x: el.scrollWidth > el.clientWidth ? next.x : current.x,
        y: el.scrollHeight > el.clientHeight ? next.y : current.y,
      });
    },
    [setCenter, panelId]
  );
  // A drag that moved beyond the slop must not also open the fullscreen
  // modal; the browser fires a click after every mouseup regardless of drag
  // distance, so the capture handler consumes exactly that one click.
  const suppressNextClickRef = useRef(false);
  const onDragMovement = useCallback(
    (drag: Pick<DragSummary, 'movementX' | 'movementY'>) => {
      suppressNextClickRef.current ||=
        Math.abs(drag.movementX) > DRAG_CLICK_SUPPRESSION_PX ||
        Math.abs(drag.movementY) > DRAG_CLICK_SUPPRESSION_PX;
    },
    []
  );
  const onMouseDownCapture = useCallback((event: React.MouseEvent) => {
    // An outside release may not click this tile. Clear its pending
    // suppression on the next press, including after returning to Fit.
    if (event.button === 0) {
      suppressNextClickRef.current = false;
    }
  }, []);
  const onClickCapture = useCallback((event: React.MouseEvent) => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);
  const onViewportCenterChange = useCallback(
    (next: ViewportCenterPercent, isDragScroll: boolean) => {
      if (isDragScroll && viewportRef.current != null) {
        writeExpressibleAxes(viewportRef.current, next);
      }
    },
    [writeExpressibleAxes]
  );
  const {isPanning, onMouseDown, onMouseUp, onScroll} = useScrollCenterPan({
    containerRef: viewportRef,
    viewportCenterPercent: sharedCenter,
    setViewportCenterPercent: onViewportCenterChange,
    restoreKeys: [zoom, cardSize.width, cardSize.height],
    onUserScroll: writeExpressibleAxes,
    onDragMove: onDragMovement,
    onDragEnd: onDragMovement,
  });

  return (
    <div
      ref={viewportRef}
      data-test="compare-tile-viewport"
      onScroll={onScroll}
      onMouseDownCapture={onMouseDownCapture}
      onMouseDown={canPan ? onMouseDown : undefined}
      onMouseUp={canPan ? onMouseUp : undefined}
      onClickCapture={onClickCapture}
      className={twMerge(
        'compare-tile-viewport flex w-full h-full min-w-0 min-h-0',
        zoom === 'fit' ? 'overflow-hidden' : 'overflow-auto',
        canPan && 'select-none',
        canPan && (isPanning ? 'cursor-grabbing' : 'cursor-grab'),
        // Center only what fits: overflow before the scroll origin is
        // unreachable, so oversized content must start at that origin.
        overflowsX ? 'justify-start' : 'justify-center',
        overflowsY ? 'items-start' : 'items-center'
      )}>
      <TileMediaLoader
        {...props}
        width={Math.max(cardSize.width, 1)}
        height={Math.max(cardSize.height, 1)}
        onImageRender={onImageRender}
      />
    </div>
  );
};
